import { useForm, useWatch } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Seo } from "../../components/Seo";
import { useGyms } from "../../api/hooks";
import { api } from "../../api/client";
import { contactSchema, gymSlugSchema, type ContactPayload, type GymSlug } from "@heracles/shared";
import { OpeningHours } from "../../components/OpeningHours";
import { GymMap } from "../../components/GymMap";
import { TrialSlotPicker } from "../../components/TrialSlotPicker";
import { Turnstile, isTurnstileEnabled } from "../../components/Turnstile";
import { gymMapUrl } from "../../lib/maps";
import { track } from "../../lib/track";

const TRIAL_DEFAULT_MESSAGE =
  "Bonjour,\n\nJe souhaite profiter d'une séance d'essai offerte. Pouvez-vous me confirmer ma venue ?\n\nMerci.";

const GYM_CITY: Record<GymSlug, string> = {
  brest: "Brest",
  landerneau: "Landerneau",
};

type ContactType = "contact" | "trial" | "dropin";

// Messages pré-remplis selon le type de demande. La salle est ajoutée quand elle
// est connue (?gym=<slug> ou select). Le message par défaut suit le choix du
// type tant que le visiteur n'a rien écrit de personnel (voir plus bas).
const contactDefaultMessage = (slug?: GymSlug) =>
  `Bonjour,\n\nJe souhaite avoir plus d'informations sur CrossFit Héraclès${
    slug ? ` à ${GYM_CITY[slug]}` : ""
  }.\n\nMerci de me recontacter.`;

const dropinDefaultMessage = (slug?: GymSlug) =>
  `Bonjour,\n\nJe suis de passage et souhaite réaliser un drop-in${
    slug ? ` à ${GYM_CITY[slug]}` : ""
  }.\n\nMa date de venue et mon niveau : \n\nMerci.`;

const defaultMessageFor = (type: ContactType, slug?: GymSlug) =>
  type === "trial"
    ? TRIAL_DEFAULT_MESSAGE
    : type === "dropin"
      ? dropinDefaultMessage(slug)
      : contactDefaultMessage(slug);

export function Contact() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const initialType: ContactType =
    typeParam === "trial" ? "trial" : typeParam === "dropin" ? "dropin" : "contact";
  const querySubject = searchParams.get("subject") ?? "";
  const queryMessage = searchParams.get("message") ?? "";
  const { data: gyms } = useGyms();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const trialBlockRef = useRef<HTMLDivElement | null>(null);

  // ?gym=<slug> : valeur initiale du select uniquement (jamais resynchronisée).
  const querySlug: GymSlug | undefined = (() => {
    const parsed = gymSlugSchema.safeParse(searchParams.get("gym"));
    return parsed.success ? parsed.data : undefined;
  })();

  const initialMessage = queryMessage || defaultMessageFor(initialType, querySlug);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ContactPayload>({
    defaultValues: {
      type: initialType,
      gym: querySlug,
      subject: querySubject,
      message: initialMessage,
    },
  });

  const watchedType = useWatch({ control, name: "type" });
  const watchedGym = useWatch({ control, name: "gym" });
  const watchedTrialSlot = useWatch({ control, name: "trialSlot" });
  const isTrial = watchedType === "trial";

  const selectedGym = gyms?.find((g) => g.slug === watchedGym);

  useEffect(() => {
    if (!isTrial && watchedTrialSlot) {
      setValue("trialSlot", undefined);
    }
  }, [isTrial, watchedTrialSlot, setValue]);

  // Quand le visiteur choisit un type de demande, on adapte le message par
  // défaut — sauf s'il a déjà écrit quelque chose de personnel. « Personnel » =
  // le message n'est ni vide ni l'un des textes par défaut connus (toutes
  // combinaisons type × salle).
  useEffect(() => {
    const current = getValues("message");
    const isPristine =
      !current ||
      (["contact", "trial", "dropin"] as const).some((t) =>
        [watchedGym as GymSlug | undefined, querySlug, undefined].some(
          (slug) => defaultMessageFor(t, slug) === current,
        ),
      );
    if (isPristine) {
      setValue("message", defaultMessageFor(watchedType, watchedGym ?? querySlug));
    }
    // Volontairement déclenché par le seul changement de type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedType]);

  const onSubmit = async (values: ContactPayload) => {
    setError(null);
    const cleaned = {
      ...values,
      trialSlot: values.type === "trial" ? values.trialSlot || undefined : undefined,
      trialSlotDate: values.type === "trial" ? values.trialSlotDate || undefined : undefined,
      turnstileToken: captchaToken ?? undefined,
    };
    const parsed = contactSchema.safeParse(cleaned);
    if (!parsed.success) {
      setError("Formulaire invalide");
      return;
    }
    try {
      await api("/contact", { method: "POST", json: parsed.data });
      setSent(true);
      reset({
        type: initialType,
        gym: querySlug,
        subject: querySubject,
        message: initialMessage,
        trialSlot: undefined,
        trialSlotDate: undefined,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erreur lors de l'envoi";
      setError(
        message === "CaptchaFailed"
          ? "La vérification anti-robot a échoué, merci de réessayer."
          : message,
      );
      setCaptchaToken(null);
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Contactez CrossFit Héraclès à Brest ou Landerneau. Réservez votre séance d'essai offerte."
        path="/contact"
      />

      <section className="bg-paper py-10 text-ink-900">
        <div className="container">
          <span className="eyebrow">Contact</span>
          <h1 className="h-hero mt-2">Une question&nbsp;? Écrivez-nous</h1>
        </div>
      </section>

      <section className="section">
        <div className="container grid gap-10 md:grid-cols-[2fr_1fr]">
          <div className="min-w-0">
            {sent ? (
              <div className="rounded border border-emerald-500 bg-emerald-50 p-6">
                <h2 className="font-display text-xl font-bold">Merci !</h2>
                <p className="mt-2 text-sm text-ink-700">
                  Votre message a bien été envoyé. Nous vous répondrons dans les plus brefs délais.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <input type="text" {...register("website")} className="hidden" tabIndex={-1} autoComplete="off" />

                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Prénom" error={errors.firstName?.message}>
                    <input
                      {...register("firstName", { required: "Requis" })}
                      className="input"
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label="Nom" error={errors.lastName?.message}>
                    <input
                      {...register("lastName", { required: "Requis" })}
                      className="input"
                      autoComplete="family-name"
                    />
                  </Field>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Email" error={errors.email?.message}>
                    <input
                      type="email"
                      {...register("email", { required: "Requis" })}
                      className="input"
                      autoComplete="email"
                    />
                  </Field>
                  <Field label="Téléphone (facultatif)">
                    <input
                      type="tel"
                      {...register("phone")}
                      className="input"
                      autoComplete="tel"
                    />
                  </Field>
                </div>

                <Field label="Salle concernée" error={errors.gym?.message}>
                  <select
                    {...register("gym", { required: "Sélectionnez la salle concernée" })}
                    className="input"
                  >
                    <option value="">— Choisissez une salle —</option>
                    {gyms?.map((g) => (
                      <option key={g.id} value={g.slug}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Sujet (facultatif)">
                  <input {...register("subject")} className="input" />
                </Field>

                <Field label="Votre message" error={errors.message?.message}>
                  <textarea
                    rows={6}
                    {...register("message", { required: "Requis" })}
                    className="input"
                  />
                </Field>

                <div className="grid gap-2 sm:grid-cols-3">
                  {(
                    [
                      {
                        value: "contact",
                        title: "Demande d'information",
                        hint: "Tarifs, horaires, formules…",
                      },
                      {
                        value: "trial",
                        title: "Séance d'essai offerte",
                        hint: "Choisis un créneau dans le planning",
                      },
                      {
                        value: "dropin",
                        title: "Drop-in",
                        hint: "Réaliser un drop-in dans une salle",
                      },
                    ] as const
                  ).map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm transition ${
                        watchedType === opt.value
                          ? "border-brand-500 bg-brand-50"
                          : "border-ink-300 bg-white hover:border-brand-500"
                      }`}
                    >
                      <input
                        type="radio"
                        value={opt.value}
                        {...register("type")}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block font-semibold">{opt.title}</span>
                        <span className="block text-xs text-ink-500">{opt.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>

                {isTrial && (
                  <div
                    ref={trialBlockRef}
                    className="min-w-0 overflow-hidden rounded-md border border-ink-300 bg-ink-100 p-3 sm:p-4"
                  >
                    <h3 className="font-display text-sm font-bold uppercase">
                      Choisis ton créneau d'essai
                    </h3>
                    <p className="mt-1 text-xs text-ink-500">
                      Sélectionne un jour puis un créneau dans le planning. Nous confirmerons
                      ta venue par retour de mail.
                    </p>
                    <input
                      type="hidden"
                      {...register("trialSlot", {
                        validate: (value) =>
                          !isTrial || !!value || "Sélectionnez un jour et un créneau",
                      })}
                    />
                    <input type="hidden" {...register("trialSlotDate")} />
                    <div className="mt-4">
                      <TrialSlotPicker
                        gymSlug={watchedGym || undefined}
                        value={watchedTrialSlot}
                        onChange={(label, date) => {
                          setValue("trialSlot", label, { shouldDirty: true, shouldValidate: true });
                          setValue("trialSlotDate", date, { shouldDirty: true });
                        }}
                      />
                    </div>
                    {errors.trialSlot && (
                      <p className="mt-2 text-xs text-brand-500">{errors.trialSlot.message}</p>
                    )}
                  </div>
                )}

                {error && <p className="text-sm text-brand-500">{error}</p>}

                <button
                  disabled={isSubmitting || (isTurnstileEnabled && !captchaToken)}
                  className="btn-primary w-full sm:w-auto"
                >
                  {isSubmitting ? "Envoi…" : "Envoyer"}
                </button>
              </form>
            )}
          </div>

          <aside className="space-y-6">
            {selectedGym ? (
              <>
                <div className="card">
                  <h2 className="font-display text-lg font-bold uppercase">{selectedGym.name}</h2>
                  <address className="mt-2 not-italic text-sm text-ink-700">
                    <a
                      href={gymMapUrl(selectedGym)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track({ type: "directions_click", gym: selectedGym.slug })}
                      className="inline-flex items-start gap-1.5 hover:text-brand-500"
                    >
                      <svg
                        className="mt-0.5 h-4 w-4 shrink-0 text-brand-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>{selectedGym.address}</span>
                    </a>
                    <br />
                    <a
                      href={`tel:${selectedGym.phone.replace(/\s/g, "")}`}
                      onClick={() => track({ type: "phone_click", gym: selectedGym.slug })}
                      className="font-semibold"
                    >
                      {selectedGym.phone}
                    </a>
                    {selectedGym.email && (
                      <>
                        <br />
                        <a
                          href={`mailto:${selectedGym.email}`}
                          onClick={() => track({ type: "email_click", gym: selectedGym.slug })}
                          className="font-semibold"
                        >
                          {selectedGym.email}
                        </a>
                      </>
                    )}
                  </address>
                  <OpeningHours
                    hours={selectedGym.openingHours}
                    className="mt-3 text-xs text-ink-500"
                  />
                </div>
                <GymMap gym={selectedGym} />
              </>
            ) : (
              <p className="rounded-md border border-dashed border-ink-300 p-4 text-sm text-ink-500">
                Choisissez une salle pour afficher ses coordonnées et son plan d'accès.
              </p>
            )}

            {isTurnstileEnabled && <Turnstile onVerify={setCaptchaToken} />}
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-brand-500">{error}</span>}
    </label>
  );
}
